import { FC, MouseEvent, useState } from "react";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import Text from "@/components/Text";
import { hexToRgba } from "@/utils/theme";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { HighlightResource } from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import HighlightsModal from "./HighlightsModal";

const HighlightCard = styled.div<{ $color: string }>`
  background: rgba(226, 159, 13, 0.1);
  border-radius: 4px;
  padding: 8px 12px 6px;
  margin-bottom: 8px;
  background-color: ${(props) => hexToRgba(props.$color, 0.1)};
  transition: opacity ease-out 0.2s;
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  display: inline-block;
  &:last-child {
    margin-bottom: 0;
  }
  ${Text} {
    color: ${(props) => props.$color};
  }
`;

interface Props {
  highlight: HighlightResource;
  highlightsColors?: string[];
}

const HighlightsTableRow: FC<Props> = ({ highlight, highlightsColors }) => {
  const [showHighlightsModal, setShowHighlightsModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { teamId } = useParams();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this highlight?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/highlights/${highlight.id}`
        );
        alerts.success("Highlight successfully deleted");

        queryClient.invalidateQueries(["script_highlights", { teamId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <HighlightCard $color={highlight.color}>
          <Text $type="smallBold">{highlight.title}</Text>
        </HighlightCard>
      </Td>
      <Td>{highlight.value}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowHighlightsModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {showHighlightsModal && (
          <HighlightsModal
            onClose={() => setShowHighlightsModal(false)}
            highlightsColors={highlightsColors}
            highlight={highlight}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default HighlightsTableRow;
